import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { Col, Row, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from 'components/loading-overlay/LoadingOverlay';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import {
  getClients,
  getClientBarcode,
  getClientsByZip,
  getFedexServiceTypes
} from 'api/api';
import ZebraBrowserPrintWrapper from 'zebra-browser-print-wrapper';
import SearchBox from 'components/common/SearchBox';

const customStyles = {
  control: provided => ({
    ...provided,
    fontSize: '12px'
  }),
  placeholder: provided => ({
    ...provided,
    fontSize: '12px'
  })
};

function BulkPrint() {
  const navigate = useNavigate();
  const animatedComponents = makeAnimated();
  const [error, setError] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sourceList, setSourceList] = useState([]);
  const [selectedList, setSelectedList] = useState([]);
  const [selectedSource, setSelectedSource] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [clearButtonVisibility, setClearButtonVisibility] = useState(false);
  const [fedexServiceType, setFedexServiceType] = useState([]);
  const [selectedServiceType, setSelectedFedexServiceType] = useState(null);

  let breadcrumbItems = [];

  breadcrumbItems = [
    {
      label: 'Clients',
      url: '/clients/clients'
    },
    {
      label: 'Add',
      active: true
    }
  ];

  // Load clients
  useEffect(() => {
    loadData();
    loadFedexServiceTypes();
  }, []);

  useEffect(() => {
    if (fedexServiceType && fedexServiceType.length > 0) {
      setSelectedFedexServiceType({
        value: fedexServiceType[0].id,
        label: fedexServiceType[0].name
      });
    }
  }, [fedexServiceType]);

  const handlePrint = async () => {
    try {
      setError([]);
      setLoading(true);
      const browserPrint = new ZebraBrowserPrintWrapper();

      const defaultPrinter = await browserPrint.getDefaultPrinter();

      browserPrint.setPrinter(defaultPrinter);

      const printerStatus = await browserPrint.checkPrinterStatus();

      // Check if the printer is ready
      if (printerStatus.isReadyToPrint) {
        for (const e of selectedList) {
          try {
            const labelObject = await getBarcode(e.id);
            if (labelObject['return-label'] || labelObject['shipping-label']) {
              if (labelObject['return-label']) {
                await browserPrint.print(labelObject['return-label']);
              }

              if (labelObject['shipping-label']) {
                await browserPrint.print(labelObject['shipping-label']);

                setSelectedList(prevList =>
                  prevList.filter(item => item.id !== e.id)
                );
              }
            } else {
              setError(prevErrors => [
                ...prevErrors,
                `Error Creating the Shipping Label for ${e.name}`
              ]);
            }
          } catch (error) {
            setError(prevErrors => [
              ...prevErrors,
              `Error Printing the Shipping Label for ${e.name}`
            ]);
          }
        }
      } else {
        setError([printerStatus.errors]);
        setLoading(false);
      }
    } catch (error) {
      setError([error.message]);
      setLoading(false);
    }

    setLoading(false);
  };

  const getBarcode = async id => {
    try {
      const response = await getClientBarcode(id, selectedServiceType.value);
      return response.data.data;
    } catch (error) {
      setError([error.message]);
      return null;
    }
  };
  const loadData = async () => {
    setError([]);
    setLoading(true);

    await getClients(1, 20)
      .then(response => {
        const mappedData = response.data.data.clients.map(item => ({
          id: item.id,
          name: item.name
        }));

        setSourceList(mappedData);
      })
      .catch(err => {
        try {
          if (err.response.status === 401) {
            navigate('/sign-in');
          }
          setError(err.response.data.errors);
        } catch (ex) {
          setError(['Please try again later...']);
        }
      });

    setLoading(false);
  };

  const loadFedexServiceTypes = async () => {
    setError([]);
    setLoading(true);

    await getFedexServiceTypes()
      .then(response => {
        const mappedData = response.data.data.map(item => ({
          id: item.id,
          name: item.name
        }));

        setFedexServiceType(mappedData);
      })
      .catch(err => {
        try {
          if (err.response.status === 401) {
            navigate('/sign-in');
          }
          setError(err.response.data.errors);
        } catch (ex) {
          console.log(ex);
          setError(['Please try again later...']);
        }
      });

    setLoading(false);
  };

  const loadDataByZip = async zip => {
    setError([]);
    setLoading(true);

    await getClientsByZip(zip)
      .then(response => {
        const mappedData = response.data.data.map(item => ({
          id: item.id,
          name: item.name
        }));

        setSourceList(mappedData);
      })
      .catch(err => {
        try {
          if (err.response.status === 401) {
            navigate('/sign-in');
          }
          setError(err.response.data.errors);
        } catch (ex) {
          setError(['Please try again later...']);
        }
      });

    setLoading(false);
  };

  const moveToSelected = () => {
    const itemsToMove = sourceList.filter(item =>
      selectedSource.includes(item.id.toString())
    );

    if (itemsToMove.length > 0) {
      setSelectedList([...selectedList, ...itemsToMove]);
      setSourceList(
        sourceList.filter(item => !selectedSource.includes(item.id.toString()))
      );
      setSelectedSource([]);
    }
  };

  const moveToSource = () => {
    const itemsToMove = selectedList.filter(item =>
      selectedTarget.includes(item.id.toString())
    );

    if (itemsToMove.length > 0) {
      setSourceList([...sourceList, ...itemsToMove]);
      setSelectedList(
        selectedList.filter(
          item => !selectedTarget.includes(item.id.toString())
        )
      );
      setSelectedTarget([]);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    loadData();
    setClearButtonVisibility(false);
  };

  const handleSearchInputChange = e => {
    setSearchText(e.target.value);
    setClearButtonVisibility(true);

    if (/^\d{5}(-\d{4})?$/.test(e.target.value)) {
      loadDataByZip(e.target.value);
    }
  };

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
          <div>
            <h2 className="mb-2">Bulk Print</h2>
          </div>
        </div>
        <div className="mb-4">
          <div>
            {Object.entries(error).length > 0 &&
              error.map((e, index) => (
                <Alert variant="soft-danger" key={index}>
                  {e}
                </Alert>
              ))}
          </div>

          <div className="d-flex flex-wrap gap-3 ">
            <div style={{ width: '325px' }}>
              <label
                htmlFor="service-type"
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '600'
                }}
              >
                FedEx Service Type
              </label>
              <Select
                classNamePrefix="react-select"
                closeMenuOnSelect={true}
                components={animatedComponents}
                options={
                  fedexServiceType &&
                  fedexServiceType.map(c => ({
                    value: c.id,
                    label: c.name
                  }))
                }
                placeholder={'Select FedEx service type'}
                value={selectedServiceType}
                styles={customStyles}
                onChange={e => setSelectedFedexServiceType(e)}
                required
                name="service-type"
                menuPlacement="auto"
              />
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3" style={{ marginTop: '20px' }}>
            <div>
              <SearchBox
                placeholder="Search Clients by ZIP"
                onChange={handleSearchInputChange}
                value={searchText}
              />
            </div>

            <div style={{ width: '145px' }}>
              <Button
                variant="phoenix-secondary"
                style={{
                  visibility:
                    clearButtonVisibility === true ? 'visible' : 'hidden'
                }}
                onClick={() => handleClearFilters()}
              >
                Clear Filters
              </Button>
            </div>
          </div>
          {/* 
          <div className="d-flex flex-wrap gap-3">
            <div style={{ width: '325px' }}>
              <label
                htmlFor="service-type"
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontWeight: '600'
                }}
              >
                FedEx Service Type
              </label>
              <Select
                classNamePrefix="react-select"
                closeMenuOnSelect={true}
                components={animatedComponents}
                options={
                  fedexServiceType &&
                  fedexServiceType.map(c => ({
                    value: c.id,
                    label: c.name
                  }))
                }
                placeholder={'Select FedEx service type'}
                value={selectedServiceType}
                styles={customStyles}
                onChange={e => setSelectedFedexServiceType(e)}
                required
                name="service-type"
                menuPlacement="auto"
              />
            </div> 
          </div>*/}
        </div>
        <Row className="g-5">
          <Col xs={12} xl={8}>
            <div>
              {loading && <LoadingOverlay />}

              <div>
                <Row className="justify-content-between">
                  {/* Source List */}
                  <Col md={5}>
                    <div className="list-container">
                      <h3>Clients List</h3>
                      <select
                        multiple
                        style={{ width: '100%', height: '400px' }}
                        className="styled-select"
                        onChange={e =>
                          setSelectedSource(
                            Array.from(
                              e.target.selectedOptions,
                              option => option.value
                            )
                          )
                        }
                      >
                        {sourceList.map((item, index) => (
                          <option
                            key={item.id}
                            value={item.id}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? '#ffffff' : '#d3d3d3'
                            }}
                          >
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Col>

                  {/* Arrow Buttons */}
                  <Col
                    md={2}
                    className="d-flex flex-column justify-content-center align-items-center"
                  >
                    <Button
                      onClick={moveToSelected}
                      variant="primary"
                      style={{ width: '125px', marginBottom: '10px' }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      ADD
                    </Button>
                    <Button
                      onClick={moveToSource}
                      variant="danger"
                      style={{ width: '125px' }}
                    >
                      <FontAwesomeIcon icon={faMinus} className="me-2" />
                      REMOVE
                    </Button>
                  </Col>

                  {/* Selected List */}
                  <Col md={5}>
                    <div className="list-container">
                      <h3>Print List</h3>
                      <select
                        multiple
                        style={{ width: '100%', height: '400px' }}
                        onChange={e =>
                          setSelectedTarget(
                            Array.from(
                              e.target.selectedOptions,
                              option => option.value
                            )
                          )
                        }
                      >
                        {selectedList.map((item, index) => (
                          <option
                            key={item.id}
                            value={item.id}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? '#ffffff' : '#d3d3d3'
                            }}
                          >
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Col>
                </Row>

                <Row
                  className="justify-content-end"
                  style={{ marginTop: '10px' }}
                >
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      className="w-100"
                      variant="phoenix-secondary"
                      type="button"
                      onClick={() => navigate('/clients/clients')}
                    >
                      Discard
                    </Button>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      onClick={() => handlePrint()}
                    >
                      Print
                    </Button>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default BulkPrint;
